import { jolieConfig as config } from "../config/jolie";

export type JolieMode =
	"idle" | "walk" | "sleep" | "alert" | "watch" | "ready" | "scratch";
export type PointerPosition = { x: number; y: number } | null;
export type Viewport = { width: number; ground: number };

const reactions = ["alert", "watch", "ready", "scratch"] as const;

export function pointerDistance(
	x: number,
	pointer: PointerPosition,
	viewport: Viewport,
) {
	if (!pointer) return Infinity;
	const dx = Math.max(
		Math.abs(pointer.x - x) - config.bodyHalfWidth * config.displayScale,
		0,
	);
	const dy = Math.max(
		viewport.ground - config.bodyHeight * config.displayScale - pointer.y,
		pointer.y - viewport.ground,
		0,
	);
	return Math.hypot(dx, dy);
}

export class JolieBehavior {
	x: number;
	facing: "left" | "right" = "right";
	mode: JolieMode = "idle";
	elapsedMs = 0;
	private destination: number;
	private restRemainingMs: number;
	private walkRemainingMs = 0;
	private touchScratchRemainingMs = 0;
	private speed = config.speedPxPerSecond;

	constructor(
		width: number,
		private random = Math.random,
	) {
		this.x = width * 0.3;
		this.destination = this.x;
		this.restRemainingMs = this.randomPause();
	}

	private randomPause() {
		return (
			config.idleMinMs +
			this.random() * (config.idleMaxMs - config.idleMinMs)
		);
	}

	private setMode(mode: JolieMode) {
		if (this.mode === mode) return;
		this.mode = mode;
		this.elapsedMs = 0;
	}

	get isTouchScratching() {
		return this.touchScratchRemainingMs > 0;
	}

	finishEntrance() {
		this.setMode("idle");
		this.elapsedMs = 0;
		this.restRemainingMs =
			this.randomPause() * config.entrance.idleDurationScale;
	}

	touch(
		point: NonNullable<PointerPosition>,
		viewport: Viewport,
		durationMs: number,
	) {
		if (pointerDistance(this.x, point, viewport) > 0) return false;
		if (Math.abs(point.x - this.x) > config.facingDeadZonePx) {
			this.facing = point.x < this.x ? "left" : "right";
		}
		this.touchScratchRemainingMs = durationMs;
		this.setMode("scratch");
		this.elapsedMs = 0;
		return true;
	}

	private restAfterWalk() {
		if (this.random() < config.sleepChance) {
			this.restRemainingMs =
				config.sleepMinMs +
				this.random() * (config.sleepMaxMs - config.sleepMinMs);
			this.setMode("sleep");
		} else {
			this.restRemainingMs = this.randomPause();
			this.setMode("idle");
		}
	}

	update(
		deltaMs: number,
		pointer: PointerPosition,
		viewport: Viewport,
		reducedMotion = false,
	) {
		const dt = Math.max(0, Math.min(deltaMs, config.maxFrameDeltaMs));
		this.elapsedMs += dt;
		const margin = Math.min(
			viewport.width / 2,
			config.maximumHalfWidth * config.displayScale +
				config.edgePaddingPx,
		);
		const right = viewport.width - margin;
		this.x = Math.max(margin, Math.min(right, this.x));
		this.destination = Math.max(margin, Math.min(right, this.destination));

		// A tap plays a complete strike, even after the finger has been lifted.
		if (this.isTouchScratching) {
			this.touchScratchRemainingMs = Math.max(
				0,
				this.touchScratchRemainingMs - dt,
			);
			if (!this.isTouchScratching) {
				this.setMode("idle");
				this.restRemainingMs = this.randomPause();
			}
			return;
		}

		const distance = pointerDistance(this.x, pointer, viewport);
		let reaction: (typeof reactions)[number] | undefined;
		for (const name of reactions) {
			if (distance <= config.proximityPx[name]) reaction = name;
		}
		const previous = reactions.find((name) => name === this.mode);
		if (
			previous &&
			(!reaction ||
				reactions.indexOf(reaction) < reactions.indexOf(previous)) &&
			distance <= config.proximityPx[previous] + config.hysteresisPx
		) {
			reaction = previous;
		}
		if (reaction && pointer) {
			if (Math.abs(pointer.x - this.x) > config.facingDeadZonePx) {
				this.facing = pointer.x < this.x ? "left" : "right";
			}
			this.setMode(reaction);
			return;
		}

		if (previous || reducedMotion) {
			this.setMode("idle");
			this.restRemainingMs = this.randomPause();
		}
		if (reducedMotion) return;

		if (this.mode === "idle" || this.mode === "sleep") {
			this.restRemainingMs -= dt;
			if (this.restRemainingMs > 0 || right - margin < 1) return;
			this.destination = margin + this.random() * (right - margin);
			if (Math.abs(this.destination - this.x) < config.minimumTravelPx) {
				this.destination = this.x < viewport.width / 2 ? right : margin;
			}
			this.speed = config.speedPxPerSecond * (0.8 + this.random() * 0.4);
			this.walkRemainingMs =
				config.walkMinMs +
				this.random() * (config.walkMaxMs - config.walkMinMs);
			this.facing = this.destination < this.x ? "left" : "right";
			this.setMode("walk");
			return;
		}

		const remaining = this.destination - this.x;
		const step = (this.speed * Math.min(dt, this.walkRemainingMs)) / 1000;
		this.walkRemainingMs -= dt;
		if (Math.abs(remaining) <= step) {
			this.x = this.destination;
			this.restAfterWalk();
		} else {
			this.x += Math.sign(remaining) * step;
			if (this.walkRemainingMs <= 0) this.restAfterWalk();
		}
	}
}

type AnimationAtlas = {
	frames: Record<string, { duration: number }>;
	animations: Record<string, { frames: string[] }>;
};

export function selectJolieFrame(
	atlas: AnimationAtlas,
	state: Pick<JolieBehavior, "mode" | "facing" | "elapsedMs">,
	reducedMotion = false,
) {
	const scratch = atlas.animations.scratch.frames;
	if (state.mode === "alert") return scratch[0];
	if (state.mode === "watch") return scratch[1];
	if (state.mode === "ready") return scratch[2];
	const frames =
		state.mode === "scratch"
			? scratch.slice(3, 6)
			: atlas.animations[
					state.mode === "walk"
						? `walk_${state.facing}`
						: state.mode === "sleep"
							? "sleep"
							: "idle"
				].frames;
	if (reducedMotion) return frames[0];
	const duration = frames.reduce(
		(sum, name) => sum + atlas.frames[name].duration,
		0,
	);
	let time = state.elapsedMs % duration;
	for (const name of frames) {
		time -= atlas.frames[name].duration;
		if (time < 0) return name;
	}
	return frames[0];
}
